import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import SizePills from "../size-pills/index";
import { fetchAvailability as fetchMembershipAvailability } from "slices/pdp-membership-slice";
import useMembershipError from "./add-to-bag/use-membership-error";
import AddToBagError from "./add-to-bag/add-to-bag-error";
import AddToBag from "./add-to-bag/index";
import AtomErrorNotification from "components/source/atoms/atom-error-notification";
import MembershipAddSwapCTA from "components/source/membership/membership-add-swap-cta";
import { Faq } from "routes";
import { hasExtraSpot } from "helpers/membership-helpers";
import FitRecommendation from "components/source/membership/fit/fit-recommendation";
import FitGuarantee from "../../shared/fit-guarantee/fit-guarantee";
import { ATB_ERROR_ID, membershipLens, ASSORTMENTS, flagsAndExperimentNames } from "rtr-constants";
import AddressZipUpdater from "../address-zip-updater/address-zip-updater";
import { setSelectedSizeSku } from "slices/pdp-slice";
import { isSelectedSizeUnavailable, hasNoAvailableSkus } from "./helpers/availability-helper";
import { isNotifyMeAvailableForNewPDP } from "helpers/notify-me-helpers";
import { isOneSize } from "../helpers/disco-product-helper";
import { useOnLoadFlagOrExperiment } from "hooks/use-flags-and-experiments";
import { fetchProratedPrice } from "slices/prorated-price-slice";
import SubscriptionPromoBanner from "../../shared/promo_pills/subscription-promo-banner";
import LoadingState from "./loading-state";

const MembershipBuyBox = () => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const staticProduct = useSelector(state => state.staticProduct);
  const membershipState = useSelector(state => state.membershipState);
  const ceBagCount = useSelector(state => state.ceBagCount);
  const depotId = useSelector(state => state.workingFilters?.depotId);
  const userData = useSelector(state => state.userData);
  const proratedPrice = useSelector(state => state.proratedPrice);
  const proratedAddonPrice = proratedPrice?.addonPrice;
  const membershipStateLoading = useSelector(state => state.membershipStateLoading);

  /* TODO: Remove hardcoding of depotId when we figure out filtering mechanism based on location */
  const membershipAvailability = useSelector(state => ({
    availability: state.pdp?.membership?.availability?.items,
    availabilityLoaded: state.pdp?.membership?.availabilityLoaded,
    availabilityError: state.pdp?.membership?.availabilityError,
  }));

  const categoryRoot = useSelector(state => state.staticProduct?.categoryRoot);

  const [addonPriceFF] = useOnLoadFlagOrExperiment(
    flagsAndExperimentNames.SWAT_1162_PRORATED_ADDON_PRICE_FEATURE_TOGGLE
  );

  const dispatch = useDispatch();
  const membershipError = useMembershipError();
  const fetchedDepotIdRef = useRef(null);

  useEffect(() => {
    if (depotId && membershipStateLoading === false) {
      if (fetchedDepotIdRef.current !== depotId) {
        dispatch(fetchMembershipAvailability({ productId: staticProduct.productId, depotIds: depotId }));
        fetchedDepotIdRef.current = depotId;
      }
    }
  }, [depotId, staticProduct.productId, dispatch, membershipStateLoading]);

  useEffect(() => {
    setIsLoading(!membershipAvailability?.availabilityLoaded);
  }, [membershipStateLoading, membershipAvailability?.availabilityLoaded]);

  useEffect(() => {
    dispatch(fetchProratedPrice({ membershipState, userData }));
  }, [membershipState, userData, dispatch]);

  const handleSizeSelect = size => {
    setSelectedSize(size);
    if (size?.sku) {
      dispatch(setSelectedSizeSku(size.sku));
    }
  };

  const extraSpotBannerText = () => {
    const addonPrice = proratedAddonPrice && addonPriceFF ? proratedAddonPrice : null;
    const extraSpotPrice = hasExtraSpot(membershipState, ceBagCount, addonPrice);
    if (!extraSpotPrice) return "";

    return `You've filled all your spots. To upgrade your plan and rent this item now for ${extraSpotPrice}, add to bag.`;
  };

  const styleUnavailable = hasNoAvailableSkus(membershipAvailability, staticProduct.skus);
  const hideAvailability = membershipError?.id === ATB_ERROR_ID.INELIGBLE_EARLY_ACCESS;

  const selectedSizeUnavailable = isSelectedSizeUnavailable(selectedSize?.sku, membershipAvailability);
  const productIsEligbleForUser = membershipError?.id !== ATB_ERROR_ID.INELIGBLE_ITEM_DUE_TO_BASIC_PLAN;
  const oneSizeProduct = isOneSize(staticProduct);
  const isNotifyMeEligible = isNotifyMeAvailableForNewPDP(
    productIsEligbleForUser,
    styleUnavailable,
    selectedSizeUnavailable,
    oneSizeProduct
  );

  const isWomensAccessories = categoryRoot === ASSORTMENTS.WOMENS_ACCESSORIES;

  const renderErrorMessage = () => {
    if (membershipError?.type === "swap_notification") {
      return <MembershipAddSwapCTA membershipState={membershipState} />;
    }

    if (membershipError?.type === "notification") {
      return (
        <AtomErrorNotification
          additionalClassName={membershipError.additionalClassName}
          errorCopy={membershipError.text}
          errorCTA={membershipError.ctaText}
          errorURL={membershipError.errorURL}
          onClick={membershipError.onClickCta}
        />
      );
    }

    if (styleUnavailable) {
      return (
        <AtomErrorNotification
          additionalClassName="available-soon"
          errorCopy="This item will be available soon! Heart it now so you can rent it later."
          errorCTA="Learn More"
          errorURL={Faq.notAvailable}
        />
      );
    }

    if (extraSpotBannerText()) {
      return <AtomErrorNotification additionalClassName="available-soon bg-beige" errorCopy={extraSpotBannerText()} />;
    }

    return null;
  };

  return (
    <>
      <LoadingState isLoading={isLoading} />
      <section
        className={`mt-6 ${isLoading ? "hidden" : ""}`}
        data-test-id="membership-buy-box"
        aria-labelledby="membership-add-to-bag">
        <div className="mb-6">
          <SubscriptionPromoBanner />
        </div>
        <SizePills
          skus={staticProduct.skus}
          availability={hideAvailability ? [] : membershipAvailability.availability}
          availabilityLoaded={membershipAvailability.availabilityLoaded}
          availabilityError={membershipAvailability.availabilityError}
          onSelect={handleSizeSelect}
          hideOneSize={isWomensAccessories}
          selectUnavailableSize={isNotifyMeEligible}
        />

        <div className="h-4">
          <FitRecommendation className="m-0" product={staticProduct} />
        </div>

        {renderErrorMessage()}

        <AddToBagError />
        <AddToBag
          membershipError={membershipError}
          availability={membershipAvailability.availability}
          availabilityLoaded={membershipAvailability.availabilityLoaded}
          selectedSize={selectedSize}
        />
        <FitGuarantee className="my-6" lens={membershipLens.unlimited} placement="pdp" />
        <AddressZipUpdater isGridProductDrawer={false} />
      </section>
    </>
  );
};

export default MembershipBuyBox;
